import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {Typography} from '@mui/material';
import {useCallback, useEffect} from 'react';
import {useSearchParams} from 'react-router';
import AdminTradeSessions from './AdminTradeSessions';
import AdminTradeTradesSection from './AdminTradeTradesSection';
import useJournalSessions from '../../../hooks/journalSessions/useJournalSessions';

export default function AdminTradePage() {
  const {showMessage} = useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionIdFilter = searchParams.get('sessionId') ?? undefined;

  const journal = useJournalSessions();

  useEffect(() => {
    if (journal.error !== undefined) {
      showMessage(journal.error?.message ?? 'Unknown error', 6000, 'error');
    }
  }, [journal.error, showMessage]);

  const setSessionFilter = useCallback(
    (sessionId: string | undefined) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (sessionId === undefined || sessionId === '') {
          next.delete('sessionId');
        } else {
          next.set('sessionId', sessionId);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <ContentContainer data-testid="admin-trade-page">
      <Typography variant="h5" component="h2" sx={{mb: 2}}>
        Trades
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        View and manage trades captured via the Trading Journal extension.
      </Typography>

      <AdminTradeSessions
        journalSessions={journal.data}
        selectedSessionId={sessionIdFilter}
        onSelectSessionId={setSessionFilter}
        journalLoading={journal.loading}
        journalPageDepth={journal.pageIndex}
        journalHasNextPage={journal.canGoNext}
        onReloadSessions={() => void journal.refetch()}
        onSessionsPrev={journal.handlePrevPage}
        onSessionsNext={journal.handleNextPage}
      />

      {sessionIdFilter !== undefined ? (
        <AdminTradeTradesSection
          key={sessionIdFilter}
          sessionId={sessionIdFilter}
          onJournalSessionsRefetch={() => void journal.refetch()}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          Select a journal session to view trades.
        </Typography>
      )}
    </ContentContainer>
  );
}
